package top.wcpe.allincore.ui.runtime.ir.state

import top.wcpe.allincore.dsl.ir.expr.ExprNode
import top.wcpe.allincore.dsl.ir.expr.IrType
import top.wcpe.allincore.dsl.ir.expr.StmtNode
import top.wcpe.allincore.ui.runtime.internal.concurrent.blockingDelayForTest
import top.wcpe.allincore.ui.runtime.internal.log.UiLog
import top.wcpe.allincore.ui.runtime.internal.time.currentTimeMillis
import top.wcpe.allincore.ui.runtime.ir.DslRuntimeError
import top.wcpe.allincore.ui.runtime.ir.DslValueUtils
import top.wcpe.allincore.ui.runtime.ir.call.LambdaValue
import top.wcpe.allincore.ui.runtime.ir.eval.EvalContext
import top.wcpe.allincore.ui.runtime.ir.eval.ExprEvaluator
import top.wcpe.allincore.ui.runtime.ir.request.PendingRequest
import top.wcpe.allincore.ui.runtime.ir.request.RequestExecutor
import top.wcpe.allincore.ui.runtime.ir.request.SuspendContinuation
import kotlin.jvm.JvmStatic

/**
 * D2-b 类型化状态机执行器。
 *
 * 执行模型：
 * 1. 入口：从 [StmtNode.StateMachine.entryStateId] 开始执行 block。
 * 2. 每个 block：先把 record values 注入 [EvalContext] 的 var 帧，再执行 block stmts，
 *    退出帧前把当前帧 var 同步回 record（保证跨 await 可见）。
 * 3. terminator = transition：求值 payload + timeoutMs，注册 [PendingRequest]
 *    （携带 record 快照），不立即推进；后续由 `RequestCallbacks` 投递。
 * 4. terminator = terminal_natural：直接返回。
 * 5. terminator = terminal_return：求值返回值，抛 `ReturnSignal`。
 *
 * 注意：本执行器在 PendingRequest 注册前会触发 dispatcher.send；与 D1 行为一致，
 * 测试中常用的"同步 dispatcher 立即回调"必须在 send 之前 register 完成。
 */
@Suppress("TooManyFunctions")
object StateMachineExecutor {

    private val LOGGER = UiLog("top.wcpe.allincore.ui.runtime.ir.state.StateMachineExecutor")

    /** 默认请求超时（毫秒），与 [RequestExecutor] 保持一致。 */
    private const val DEFAULT_REQUEST_TIMEOUT_MS: Long = 30_000L

    /** M4 A2 Stage 3：suspend lambda 末位表达式值在 record 中的固定槽位名（与 ExprSerializer 一致）。 */
    internal const val SUSPEND_RETURN_VAR: String = "__return_value"

    /** 顶层入口：执行整个状态机，从 entryStateId 起步。 */
    @JvmStatic
    fun executeStateMachine(machine: StmtNode.StateMachine, ctx: EvalContext) {
        val record = StateRecord(LinkedHashMap(toSchemaMap(machine)))
        executeFrom(machine, machine.entryStateId, record, ctx)
    }

    /**
     * fun 形参跨 await 修复入口：在 machine 自身的 record schema 之上补登 [paramNames]，
     * 把 [paramValues] 预填到初始 record，然后从 entryStateId 起步。
     *
     * 背景：编译期 `StateMachineLowerer` 仅把 `VarDeclStmt` 纳入 record schema，
     * fun 形参不在 schema 内。若不显式补登 schema 并预填 record，第一次 await 转移之后
     * 回包路径用 `baseCtx.withLocals(__result)` 派生的全新 ctx 已不再含底帧形参，
     * record 又拿不到，再次访问 `LocalRef("形参名")` 必然抛
     * "未声明的局部变量"。本入口与 [invokeSuspendLambda] 处理 lambda
     * params/captured 的方式一致，把 fun 形参当作"运行期可见、跨状态保留"的额外槽位写入 record。
     *
     * 语义约束：
     * - [paramNames] 与 [paramValues] 长度必须一致（包含 `_` 丢弃形参的占位）。
     * - 名称为 `"_"` 的丢弃形参不入 schema、不入 record（与 lambda 路径一致）。
     * - 已存在于 machine schema 的同名条目按 [LinkedHashMap.putIfAbsent] 跳过，不覆盖编译期类型。
     *
     * [ExprEvaluator.ReturnSignal] 在 callable 边界由调用方吞掉；本入口不处理。
     */
    @JvmStatic
    fun executeStateMachineWithParams(
        machine: StmtNode.StateMachine,
        ctx: EvalContext,
        paramNames: List<String>,
        paramValues: List<Any>,
    ) {
        require(paramNames.size == paramValues.size) {
            "paramNames 长度 ${paramNames.size} 与 paramValues 长度 ${paramValues.size} 不一致"
        }
        // 在 machine 原有 schema 上补登形参槽位；保留原有顺序与类型，新增形参追加到尾部
        val extendedSchema: LinkedHashMap<String, IrType> = LinkedHashMap(toSchemaMap(machine))
        for (name in paramNames) {
            // 与 invokeSuspendLambdaStateMachine 一致：_ 丢弃形参不进入 schema
            if (name != "_") {
                if (name !in extendedSchema) extendedSchema[name] = IrType.ANY
            }
        }
        val record = StateRecord(extendedSchema)
        // 把形参预填到初始 record，使第一个 block 的 primeFrame 即可注入到 var 帧
        for (i in paramNames.indices) {
            val name = paramNames[i]
            if (name == "_") continue
            record.set(name, paramValues[i])
        }
        executeFrom(machine, machine.entryStateId, record, ctx)
    }

    /**
     * 从指定状态号继续执行；供 `RequestCallbacks` 在投递后调用。
     *
     * 对应 success/failure/timeout 三个 entry 点，本方法负责 push 帧 + 注入 record +
     * 执行 stmts + 处理 terminator + 异常时确保 pop 帧。
     *
     * 异常路径 sync 语义：
     * - 正常路径：block.stmts 执行完后按 terminator 分派；非 transition 路径走完
     *   handleTerminator 再 syncFrameToRecord，确保 var 写入回流到 record。
     * - transition 路径：sync + snapshot 由 [registerTransition] 内部在
     *   payload/timeout 求值完成后再做（捕获按引用闭包对 var 的副作用）。
     * - 异常路径（block.stmts / handleTerminator 抛异常）：finally pop 帧时不再
     *   sync —— record 保留进入本 block 前的快照值。这是有意识的设计取舍：用户
     *   逻辑出错时丢弃半执行状态比保留部分变更更安全（与 try-catch 的"未完成事务"
     *   回滚语义一致）。后续 send 抛错由 registerTransition 自己回滚 PendingRequest。
     */
    @JvmStatic
    fun executeFrom(machine: StmtNode.StateMachine, stateId: Int, record: StateRecord, ctx: EvalContext) {
        var currentId = stateId
        while (currentId >= 0) {
            val block = machine.blocks[currentId]
            var next: Int
            ctx.pushVarFrame()
            try {
                primeFrame(record, ctx)
                ExprEvaluator.executeOnCurrentFrame(block.stmts, ctx)
                next = handleTerminator(machine, block, record, ctx)
                if (block.terminatorKind != StmtNode.StateBlock.TERM_TRANSITION) {
                    syncFrameToRecord(record, ctx)
                }
            } finally {
                ctx.popVarFrame()
            }
            currentId = next
        }
    }

    /**
     * 把 record 当前 values 写入栈顶 var 帧（作为 block 同步执行的起点）。
     * 同步回写在 [syncFrameToRecord] 完成。
     */
    private fun primeFrame(record: StateRecord, ctx: EvalContext) {
        for ((key, value) in record.getValues()) {
            ctx.declareVar(key, value)
        }
    }

    /** 反向同步：把当前 var 帧中所有 record schema 中的字段写回 record。 */
    private fun syncFrameToRecord(record: StateRecord, ctx: EvalContext) {
        for (name in record.getSchema().keys) {
            if (ctx.hasVar(name)) {
                record.set(name, ctx.getVar(name))
            }
        }
    }

    /**
     * 处理 block.terminator，返回下一状态号；-1 表示状态机已结束。
     */
    @Suppress("ReturnCount", "ThrowsCount", "ComplexCondition")
    private fun handleTerminator(
        machine: StmtNode.StateMachine,
        block: StmtNode.StateBlock,
        record: StateRecord,
        ctx: EvalContext,
    ): Int {
        when (block.terminatorKind) {
            StmtNode.StateBlock.TERM_NATURAL -> {
                // M4 A2 Stage 3：suspend lambda 终态时，把 __return_value 投递给外层 continuation。
                // 必须先把当前帧 var 写回 record，否则末位 var 槽位还停留在帧上，continuation 读不到。
                val cont = record.suspendContinuation
                if (cont != null) {
                    syncFrameToRecord(record, ctx)
                    val rv = record.getValues()[SUSPEND_RETURN_VAR]
                    // 仅触发一次：清空避免内层状态机被 callback 多次回流时重复 resume
                    record.suspendContinuation = null
                    resumeWithAwaitableAwareness(cont, rv)
                }
                return -1
            }
            StmtNode.StateBlock.TERM_RETURN -> {
                val v = block.returnValue
                val value = v?.let { ExprEvaluator.eval(it, ctx) }
                throw ExprEvaluator.ReturnSignal(value)
            }
            StmtNode.StateBlock.TERM_TRANSITION -> {
                // TERM_TRANSITION 保证 transition 非 null；跨模块无法 smart-cast 公开属性，用 !! 复刻旧平台类型非空传递。
                registerTransition(machine, block.transition!!, record, ctx)
                return -1 // 等待回调
            }
            StmtNode.StateBlock.TERM_GOTO -> {
                // D2-c：无条件跳转。控制流内非 await 分支用它接合并点。
                return block.gotoNextStateId
            }
            StmtNode.StateBlock.TERM_BRANCH -> {
                // D2-c：条件跳转。控制流内 await 时把 if/while 的分支判定上提到 terminator。
                // TERM_BRANCH 保证 branchCond 非 null；跨模块无法 smart-cast 公开属性，用 !! 复刻旧平台类型非空传递。
                val cv = ExprEvaluator.eval(block.branchCond!!, ctx)
                return if (DslValueUtils.truthy(cv)) block.branchThenStateId else block.branchElseStateId
            }
            StmtNode.StateBlock.TERM_DELAY -> {
                // delay(ms)：把当前帧同步回 record，然后通过 DslExecutor.schedule 延迟恢复。
                syncFrameToRecord(record, ctx)
                // TERM_DELAY 保证 delayMs 非 null；跨模块无法 smart-cast 公开属性，用 !! 复刻旧平台类型非空传递。
                val delayExpr = block.delayMs!!
                val delayVal = ExprEvaluator.eval(delayExpr, ctx)
                if (delayVal !is Number) {
                    throw DslRuntimeError(
                        "delay() 参数必须是数字，实际为: ${if (delayVal == null) "null" else delayVal::class.simpleName}",
                    )
                }
                val delayMs: Long = maxOf(0, delayVal.toLong())
                val executor = ctx.executor
                val nextId = block.delayNextStateId
                if (executor != null) {
                    // 异步模式：延迟后在 DSL 线程恢复状态机
                    val snapshotRecord = record.snapshot()
                    val machineRef = machine
                    val ctxRef = ctx
                    executor.schedule({ executeFrom(machineRef, nextId, snapshotRecord, ctxRef) }, delayMs)
                } else {
                    // 同步模式（测试）：直接 sleep 后继续
                    blockingDelayForTest(delayMs)
                    return nextId
                }
                return -1 // 异步模式下当前执行链结束，等 schedule 恢复
            }
            else -> throw DslRuntimeError("未知 state block terminator: ${block.terminatorKind}")
        }
    }

    /**
     * 求值 transition 的 payload / timeoutMs 并注册 PendingRequest。
     *
     * 注意求值与 record 快照的时序：payload 表达式可调用按引用闭包修改 var，
     * 因此先求 payload + timeoutMs（在已 prime 过的 var 帧上），再 sync 帧值
     * 回 record，最后 snapshot 写入 PendingRequest，确保挂起前快照包含表达式
     * 副作用产生的最新 var 值。
     *
     * send() 抛错时回滚已注册 PendingRequest 并原样抛出，避免 tickTimeouts
     * 把悬挂请求当成 timeout 误恢复状态机。
     */
    @Suppress("ThrowsCount")
    internal fun registerTransition(
        machine: StmtNode.StateMachine,
        tr: StmtNode.Transition,
        record: StateRecord,
        ctx: EvalContext,
    ) {
        if (tr.transitionKind == StmtNode.Transition.KIND_SUSPEND_CALL) {
            registerSuspendCallTransition(machine, tr, record, ctx)
            return
        }
        val registry = ctx.registry ?: throw DslRuntimeError("当前上下文不支持 request：缺少 RequestRegistry / RequestDispatcher")
        val requestDispatcher =
            ctx.requestDispatcher ?: throw DslRuntimeError("当前上下文不支持 request：缺少 RequestRegistry / RequestDispatcher")
        // payload 值类型须可空：ExprEvaluator.eval 可返回 null（如 DSL null 字面量 / 值为 null 的变量），
        // 原 Java LinkedHashMap<String, Object> 允许 null 条目并投递，收紧为非空会在写入处抛 NPE。
        val payload: LinkedHashMap<String, Any?> = LinkedHashMap()
        for (entry in tr.payload) {
            payload[entry.key] = ExprEvaluator.eval(entry.value, ctx)
        }
        val timeoutMs = resolveTimeoutMs(tr.timeoutMs, ctx)
        val deadline = currentTimeMillis() + timeoutMs
        // 在求值表达式之后再同步 + 快照，捕获按引用闭包对 var 的写入。
        syncFrameToRecord(record, ctx)
        val requestId = registry.nextId()
        val pr = PendingRequest(
            requestId,
            deadline,
            machine,
            record.snapshot(),
            tr.successNextStateId,
            tr.failureNextStateId,
            tr.timeoutNextStateId,
            tr.bindVarName,
            tr.tailExpr,
        )
        registry.register(pr)
        try {
            requestDispatcher.send(requestId, tr.namespace, tr.handler, payload)
        } catch (sendError: RuntimeException) {
            registry.take(requestId)
            throw sendError
        }
    }

    /**
     * 处理 transitionKind=SUSPEND_CALL（M4 A2 Stage 3）。
     *
     * 执行步骤：
     * 1. 求值 lambdaCallee + args（仍在外层帧），lambdaCallee 必须为 `isSuspend=true` 的 [LambdaValue]。
     * 2. 把外层帧 var 同步回外层 record（与 net.request 路径对称，捕获副作用）。
     * 3. 构造内层 record（schema = suspend lambda body 的状态机 schema，预注入 captured + args），
     *    挂载 continuation：终态时把 __return_value 绑到外层 record[bindVarName]，
     *    然后从外层 successNextStateId 继续推进外层状态机。
     * 4. 同步驱动内层状态机；若内层全部是同步 stmt（如末位单一表达式），continuation 会被立即触发，
     *    本调用栈内即完成外层 success 投递；若内层含异步 await，continuation 会通过
     *    PendingRequest → RequestCallbacks → resume 链路被后续 callback 触发。
     *
     * 关键设计：不创建外层 PendingRequest，"挂起"通过 continuation 钩子在内层终态时自动回流；
     * 这样 PendingRequest 始终只对应一次真实网络请求，避免父子嵌套关系。
     */
    private fun registerSuspendCallTransition(
        outerMachine: StmtNode.StateMachine,
        tr: StmtNode.Transition,
        outerRecord: StateRecord,
        ctx: EvalContext,
    ) {
        // SUSPEND_CALL transition 保证 lambdaCallee 非 null；跨模块无法 smart-cast 公开属性，用 !! 复刻旧平台类型非空传递。
        val calleeValue = ExprEvaluator.eval(tr.lambdaCallee!!, ctx)
        // 路径分支：
        // 1. LambdaValue + isSuspend → 走原 suspend lambda 内层状态机路径
        // 2. PendingAwaitable → 任务 3 通用 await：lowerer 把 MemberCall 整体当 callee，args 为空，
        //    求值后即得 awaitable；同步回外层 record + 注册 continuation 等待 onResume
        // 3. 任意其它值 → 编译期已识别为 await 目标，但运行期返回普通值视为同步完成（立即 resume）
        val argValues = ArrayList<Any?>(tr.args.size)
        for (arg in tr.args) {
            argValues.add(ExprEvaluator.eval(arg, ctx))
        }
        syncFrameToRecord(outerRecord, ctx)

        val bindName = tr.bindVarName
        val successId = tr.successNextStateId
        val continuation: SuspendContinuation = { returnValue ->
            if (bindName != null && outerRecord.getSchema().containsKey(bindName)) {
                outerRecord.set(bindName, returnValue)
            }
            try {
                executeFrom(outerMachine, successId, outerRecord, ctx)
            } catch (_: ExprEvaluator.ReturnSignal) {
                // 状态机内部 return 在 callable 边界吞掉
            }
        }

        when (calleeValue) {
            is LambdaValue -> {
                if (!calleeValue.isSuspend) {
                    throw DslRuntimeError("await 仅可作用于 suspend lambda")
                }
                invokeSuspendLambda(calleeValue, argValues, ctx, continuation)
                return
            }
            is PendingAwaitable -> {
                // 通用 await：调用方提供的 awaitable 自己负责异步完成 + 调 continuation.resume
                resumeWithAwaitableAwareness(continuation, calleeValue)
                return
            }
            else -> {
                // 任意其它值（包括 null）：lowerer 已经把整个 MemberCall 求值过了，结果就是返回值
                continuation.resume(calleeValue)
            }
        }
    }

    /**
     * 调用 suspend lambda：构造内层 record（含 captured + 参数），挂上 continuation，
     * 启动内层状态机。同步 / 异步路径统一：
     * - 同步路径：内层 lambda 仅含末位 ExprStmt（无 await），lowerer 退化为普通 stmt 列表；
     *   此时 lambda body 不是 StateMachine，需要直接在新帧上执行末位赋值，
     *   然后立即触发 continuation。
     * - 异步路径：内层 body 含 await，lowerer 输出单条 StateMachine stmt；
     *   走 executeFrom，终态时 TERM_NATURAL 自动触发 continuation。
     */
    internal fun invokeSuspendLambda(
        lam: LambdaValue,
        argValues: List<Any?>,
        ctx: EvalContext,
        continuation: SuspendContinuation,
    ) {
        val params = lam.params
        if (params.size != argValues.size) {
            throw DslRuntimeError("suspend lambda 实参个数与形参不符：期望 ${params.size}，实际 ${argValues.size}")
        }
        val body = lam.body
        if (body.size == 1 && body[0] is StmtNode.StateMachine) {
            invokeSuspendLambdaStateMachine(lam, argValues, ctx, continuation, body[0] as StmtNode.StateMachine)
            return
        }
        // 退化路径：body 内不含 await。仍按 suspend lambda 约束，末位必须为 VarDecl(__return_value)
        // —— 由 ExprSerializer.transformSuspendLambdaBody 改写得到。我们在新帧执行 body 后读 var。
        ctx.pushVarFrame()
        try {
            for ((key, value) in lam.captured) {
                ctx.declareVar(key, value)
            }
            for (i in params.indices) {
                val name = params[i]
                // M5-a #6：`_` 是丢弃占位符，不注入到帧中。
                if (name == "_") continue
                ctx.declareVar(name, argValues[i])
            }
            ExprEvaluator.executeOnCurrentFrame(body, ctx)
            val rv = if (ctx.hasVar(SUSPEND_RETURN_VAR)) ctx.getVar(SUSPEND_RETURN_VAR) else null
            resumeWithAwaitableAwareness(continuation, rv)
        } catch (rs: ExprEvaluator.ReturnSignal) {
            // suspend lambda 内 return：把 return value 作为最终返回
            resumeWithAwaitableAwareness(continuation, rs.value)
        } finally {
            ctx.popVarFrame()
        }
    }

    /**
     * 内层 body 为单条 StateMachine 时的执行路径：构造内层 record（schema 取自 machine 自身），
     * 注入 captured / 参数 / 挂 continuation，从 entry 开始推进。
     */
    private fun invokeSuspendLambdaStateMachine(
        lam: LambdaValue,
        argValues: List<Any?>,
        ctx: EvalContext,
        continuation: SuspendContinuation,
        machine: StmtNode.StateMachine,
    ) {
        // 在 schema 基础上补上 lambda 参数和 captured 名，使其可被 primeFrame 注入 var 帧。
        // lowerer 仅登记 body 内显式 var；params / captured 在编译期不可见，必须运行时合并。
        val extendedSchema: LinkedHashMap<String, IrType> = LinkedHashMap(toSchemaMap(machine))
        for (name in lam.captured.keys) {
            if (name !in extendedSchema) extendedSchema[name] = IrType.ANY
        }
        for (p in lam.params) {
            // M5-a：_ 丢弃形参不进入 schema
            if (p != "_") {
                if (p !in extendedSchema) extendedSchema[p] = IrType.ANY
            }
        }
        val innerRecord = StateRecord(extendedSchema)
        // captured 先注入，参数后注入，与同步 lambda 一致：实参覆盖同名 captured
        for ((key, value) in lam.captured) {
            innerRecord.set(key, value)
        }
        val params = lam.params
        for (i in params.indices) {
            val name = params[i]
            // M5-a #6：`_` 是丢弃占位符，不写入 record。
            if (name == "_") continue
            innerRecord.set(name, argValues[i])
        }
        innerRecord.suspendContinuation = continuation
        try {
            executeFrom(machine, machine.entryStateId, innerRecord, ctx)
        } catch (rs: ExprEvaluator.ReturnSignal) {
            // suspend lambda 内 return 视为返回值，触发 continuation。
            // 若 continuation 已经被 TERM_NATURAL 触发，setSuspendContinuation(null) 会让此处 noop。
            val cont = innerRecord.suspendContinuation
            if (cont != null) {
                innerRecord.suspendContinuation = null
                resumeWithAwaitableAwareness(cont, rs.value)
            }
        }
    }

    /**
     * 触发 continuation 时识别 [PendingAwaitable]：返回值若实现该接口，
     * 不立即 resume，而是把 continuation 转交 awaitable，等其异步完成时由 awaitable
     * 调 resume；否则按普通返回值直接 resume。
     *
     * 这是"通用 await"语义的核心：DSL 中 `await target.method(args)` 经
     * IIFE-suspend-lambda 包装后，末位表达式求值结果若是 PendingAwaitable，状态机自动
     * 等其完成；否则按同步路径立即恢复。零编译器改动、零 IR 协议变更。
     */
    private fun resumeWithAwaitableAwareness(cont: SuspendContinuation, rv: Any?) {
        if (rv is PendingAwaitable) {
            val awaitable = rv
            // 已被取消：当作 null 返回值立即 resume，避免外层 await 永久挂起
            if (awaitable.isCancelled()) {
                cont.resume(null)
                return
            }
            try {
                awaitable.onResume { value -> cont.resume(value) }
            } catch (t: Throwable) {
                LOGGER.warn("PendingAwaitable.onResume 注册失败，已 fallback 到立即 resume(null)", t)
                cont.resume(null)
            }
            return
        }
        cont.resume(rv)
    }

    /** 同 [RequestExecutor.resolveTimeoutMs]，做必要的数值校验。 */
    @Suppress("ThrowsCount")
    private fun resolveTimeoutMs(timeoutExpr: ExprNode?, ctx: EvalContext): Long {
        if (timeoutExpr == null) return DEFAULT_REQUEST_TIMEOUT_MS
        val value = ExprEvaluator.eval(timeoutExpr, ctx)
        if (value !is Number) {
            throw DslRuntimeError("timeoutMs 必须为数值（单位：毫秒），实际: ${DslValueUtils.classOf(value)}")
        }
        val d = value.toDouble()
        if (d.isNaN() || d.isInfinite() || d < 0.0) {
            throw DslRuntimeError("timeoutMs 必须为非负有限数值（单位：毫秒），实际: $d")
        }
        val maxAllowed = Long.MAX_VALUE - currentTimeMillis()
        if (d > maxAllowed.toDouble()) {
            throw DslRuntimeError("timeoutMs 过大将导致 deadline 溢出（上限约 $maxAllowed ms），实际: $d")
        }
        return d.toLong()
    }

    /** 仅用于 record 初始化时构造空 schema map（避免显式依赖具体类型）。 */
    private fun toSchemaMap(m: StmtNode.StateMachine): LinkedHashMap<String, IrType> {
        val out: LinkedHashMap<String, IrType> = LinkedHashMap()
        for (i in m.recordSchemaNames.indices) {
            out[m.recordSchemaNames[i]] = m.recordSchemaTypes[i]
        }
        return out
    }

    /**
     * 回调端入口：从指定状态号继续执行，bindVarName / tailExpr 由调用方按约定预先注入到
     * locals 上下文。本方法不负责回调阶段的 result 注入，仅负责把后续状态序列跑完。
     */
    @JvmStatic
    internal fun resume(machine: StmtNode.StateMachine, stateId: Int, record: StateRecord, ctx: EvalContext) {
        executeFrom(machine, stateId, record, ctx)
    }

    /** 兜底空 locals。 */
    @JvmStatic
    internal fun emptyLocals(): Map<String, Any> = emptyMap()
}
